import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Plus, Edit2, Trash2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FIELDS = [
  { key: 'objetivo_tela', label: 'Objetivo da Tela', rows: 3 },
  { key: 'quando_usar', label: 'Quando Usar', rows: 2 },
  { key: 'pre_requisitos', label: 'Pré-requisitos', rows: 2 },
  { key: 'passo_a_passo', label: 'Passo a Passo Operacional', rows: 5 },
  { key: 'campos_e_botoes', label: 'Campos e Botões', rows: 4 },
  { key: 'regras_de_negocio', label: 'Regras de Negócio', rows: 3 },
  { key: 'alertas', label: 'Alertas e Cuidados', rows: 2 },
  { key: 'erros_comuns', label: 'Erros Comuns', rows: 3 },
  { key: 'boas_praticas', label: 'Boas Práticas', rows: 2 },
  { key: 'observacoes_finais', label: 'Observações Finais', rows: 2 },
];

const ScreenManualsPage = () => {
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/screen-manuals`);
      setManuals(res.data);
    } catch { toast.error('Erro ao carregar manuais'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleNew = () => {
    setForm({ screen_key: '', nome_tela: '' });
    setEditing('new');
  };

  const handleEdit = (manual) => {
    setForm({ ...manual });
    setEditing(manual.manual_id);
  };

  const handleSave = async () => {
    if (!form.screen_key || !form.nome_tela) {
      toast.error('Chave da tela e nome são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API_URL}/screen-manuals`, form);
        toast.success('Manual criado');
      } else {
        const { manual_id, screen_key, criado_em, atualizado_em, atualizado_por, atualizado_por_nome, versao, ...updateData } = form;
        await axios.put(`${API_URL}/screen-manuals/${editing}`, updateData);
        toast.success('Manual atualizado');
      }
      setEditing(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const handleTogglePublish = async (manual) => {
    try {
      const newStatus = manual.status_publicacao === 'Publicado' ? 'Rascunho' : 'Publicado';
      await axios.put(`${API_URL}/screen-manuals/${manual.manual_id}`, { status_publicacao: newStatus });
      toast.success(newStatus === 'Publicado' ? 'Manual publicado' : 'Manual despublicado');
      load();
    } catch { toast.error('Erro ao alterar status'); }
  };

  const handleDelete = async (manualId) => {
    if (!window.confirm('Excluir este manual permanentemente?')) return;
    try {
      await axios.delete(`${API_URL}/screen-manuals/${manualId}`);
      toast.success('Manual excluído');
      load();
    } catch { toast.error('Erro ao excluir'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin" /></div>;

  if (editing) {
    return (
      <div className="space-y-6" data-testid="manual-editor">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-[#111111]">{editing === 'new' ? 'Novo Manual' : 'Editar Manual'}</h1>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="cancel-manual-btn">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="save-manual-btn">
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>

        <div className="bg-white border border-[#E5E5EA] rounded-xl p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Chave da Tela (screen_key) *</Label>
              <Input
                value={form.screen_key || ''}
                onChange={e => setForm(p => ({ ...p, screen_key: e.target.value }))}
                placeholder="ex: dashboard"
                disabled={editing !== 'new'}
                className="mt-1"
                data-testid="manual-screen-key"
              />
            </div>
            <div>
              <Label>Nome da Tela *</Label>
              <Input
                value={form.nome_tela || ''}
                onChange={e => setForm(p => ({ ...p, nome_tela: e.target.value }))}
                placeholder="ex: Dashboard"
                className="mt-1"
                data-testid="manual-nome-tela"
              />
            </div>
          </div>

          {FIELDS.map(field => (
            <div key={field.key}>
              <Label>{field.label}</Label>
              <Textarea
                value={form[field.key] || ''}
                onChange={e => setForm(p => ({ ...p, [field.key]: e.target.value }))}
                rows={field.rows}
                className="mt-1"
                placeholder={`Descreva ${field.label.toLowerCase()}...`}
                data-testid={`manual-field-${field.key}`}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="screen-manuals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Manuais de Tela</h1>
          <p className="text-sm text-[#6E6E73] mt-1">Gerencie o conteúdo de ajuda contextual de cada tela</p>
        </div>
        <Button onClick={handleNew} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="new-manual-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Manual
        </Button>
      </div>

      {manuals.length > 0 ? (
        <div className="bg-white border border-[#E5E5EA] rounded-xl divide-y divide-[#E5E5EA]">
          {manuals.map(m => (
            <div key={m.manual_id} className="flex items-center justify-between px-5 py-4 hover:bg-[#F7F7F8]" data-testid={`manual-row-${m.screen_key}`}>
              <div className="flex items-center gap-3 min-w-0">
                <BookOpen className="w-5 h-5 text-[#AF2628] flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#111111] truncate">{m.nome_tela}</p>
                  <p className="text-xs text-[#AEAEB2]">
                    <span className="font-mono">{m.screen_key}</span> · v{m.versao} · {new Date(m.atualizado_em).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${m.status_publicacao === 'Publicado' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                  {m.status_publicacao}
                </span>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleTogglePublish(m)} data-testid={`toggle-publish-${m.screen_key}`}>
                  {m.status_publicacao === 'Publicado' ? <EyeOff className="w-4 h-4 text-[#6E6E73]" /> : <Eye className="w-4 h-4 text-emerald-500" />}
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(m)} data-testid={`edit-manual-${m.screen_key}`}>
                  <Edit2 className="w-4 h-4 text-[#6E6E73]" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(m.manual_id)} data-testid={`delete-manual-${m.screen_key}`}>
                  <Trash2 className="w-4 h-4 text-red-400" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5EA] rounded-xl p-10 text-center">
          <BookOpen className="w-12 h-12 mx-auto mb-3 text-[#E5E5EA]" />
          <p className="text-sm text-[#6E6E73]">Nenhum manual cadastrado</p>
          <p className="text-xs text-[#AEAEB2] mt-1">Crie manuais para ajudar os usuários a operar cada tela</p>
        </div>
      )}
    </div>
  );
};

export default ScreenManualsPage;
